<template>
    <app-wrapper>
        <app-header medium-max-width="max-w-1000" :is-hidden-menu="true">
            <template #top>
                <div class="w-full flex items-center">
                    <div class="flex-auto text-center">
                        <!-- Progress-toward-unlock copy only renders when a reward actually exists.
                             At the rate floor / paid-in-full band nothing unlocks, so promising
                             "better loan terms" or counting payments toward a target is false. -->
                        <h2 v-if="hasUnlockableRewards" class="mb-1 mt-0 md:text-4xl">You're On Your Way to Better Loan Terms!</h2>
                        <h2 v-else class="mb-1 mt-0 md:text-4xl">Your New Loan</h2>
                        <span v-if="!hasUnlockableRewards" class="text-gray-400">Cash back becomes available after your first on-time payment.</span>
                        <span v-else-if="storeData.advancedRefiFrozen && storeData.advancedRefiFrozenDaysRemaining > 0" class="text-gray-400">
                            You've paid enough for better refi options. They unlock in {{ storeData.advancedRefiFrozenDaysRemaining }} days.
                        </span>
                        <span v-else class="text-gray-400">You've completed {{ storeData.paidPayments }} of the {{ phase2TargetPayments }} payments needed to unlock additional rewards</span>
                    </div>
                </div>
            </template>
        </app-header>
        <app-body medium-max-width="max-w-1000">

            <template #main>

                <PaymentsCompletedProgressBar
                    v-if="hasUnlockableRewards"
                    :total-value="phase2TargetPayments"
                    :current-value="storeData.paidPayments"
                />

                <AppBoxContent 
                    class="my-8"
                    width="full" 
                    rounded="lg"
                    bg-class-color="bg-white" 
                    :use-min-height="false"
                    :remove-max-width="true"
                >
                    <div class="cashback-box-content-container text-[#6B7280]">
                        <p v-if="storeData.advancedRefiFrozen && storeData.advancedRefiFrozenDaysRemaining > 0" class="font-semibold mb-4 text-4xl text-black">Better Terms Unlock in {{ storeData.advancedRefiFrozenDaysRemaining }} Days</p>
                        <p v-else class="font-semibold mb-4 text-4xl text-black">Cash Back Unlocks After Your First Payment</p>
                        <h3 class="text-xl font-normal">Current Loan Balance: {{ storeData.payoff | formatMoney }}</h3>

                        <div class="mt-6">
                            <p>Once cash back unlocks:</p>
                            <ul class="list-disc pl-4 ml-2 mt-2">
                                <li>Same monthly payment</li>
                                <li>Same loan term</li>
                                <li>Cash will be added to your loan balance</li>
                            </ul>
                        </div>

                        <Alert type="warning" class="mt-6">
                            <span v-if="storeData.advancedRefiFrozen && storeData.advancedRefiFrozenDaysRemaining > 0">
                                You've paid enough for better refi options. They unlock in {{ storeData.advancedRefiFrozenDaysRemaining }} days.
                            </span>
                            <span v-else>
                                Cash back becomes available after your first on-time payment is collected.
                            </span>
                        </Alert>



                        <div>
                            <a class="bg-[#de9e52] no-underline text-white py-2 px-4 rounded-md inline-block mt-2" :href="storeData.portalUrl">Make Your First Payment</a>
                        </div>
                    </div>
                </AppBoxContent>


                <!-- Milestone promises (lower rate / cash-back bonus) only exist when something is
                     actually unlockable — hidden at the rate floor / paid-in-full band. -->
                <div v-if="hasUnlockableRewards" class="rounded-lg  border border-[#2db405] bg-[#e0e8df] shadow-2xl p-8 mb-6">
                    <h2 class="text-2xl font-bold mb-2">Your Next Milestone</h2>
                    <p v-if="storeData.advancedRefiFrozen && storeData.advancedRefiFrozenDaysRemaining > 0" class="text-gray-700 mb-4">
                        You've paid enough to unlock better terms. They'll be available in <span class="text-2xl font-bold">{{ storeData.advancedRefiFrozenDaysRemaining }}</span> days.
                    </p>
                    <p v-else class="text-gray-700 mb-4">You're <span class="text-2xl font-bold">{{ paymentsAway() }}</span> {{ paymentsAway() === 1 ? 'payment' : 'payments' }} away from unlocking:</p>

                    <ul class="list-disc pl-4 my-4">
                        <li v-if="storeData.previewBetterRate">Lower rate: {{ storeData.currentRate | formatRate }} → <FontBold>{{ storeData.previewBetterRate | formatRate }}</FontBold></li>
                        <li v-else>Higher loan amount — up to <FontBold>{{ maxLoanAmount | formatMoney }}</FontBold></li>
                        <li>Up to <FontBold>{{ storeData.previewBalanceBonus | formatMoney }}</FontBold> in cash back</li>
                        <li>Lower monthly payments (up to 24 months)</li>
                    </ul>

                    <div>
                        <a :href="storeData.portalUrl" class="bg-[#2b8189] no-underline text-white py-2 px-4 rounded-md inline-block mt-2">Continue Making Payments</a>
                        <small v-if="storeData.advancedRefiFrozen && storeData.advancedRefiFrozenDaysRemaining > 0" class="block mt-1 text-[11px]">Better rewards unlock when the age-freeze window ends</small>
                        <small v-else class="block mt-1 text-[11px]">Continue making payments to unlock better rewards</small>
                    </div>
                </div>

            </template>
        </app-body>
    </app-wrapper>
</template>

<script>
import AppBoxContent from './../../../../components/AppBoxContent.vue';
import PaymentsCompletedProgressBar from '../components/PaymentsCompletedProgressBar.vue';
import FontBold from '../components/FontBold.vue';
import Alert from '../components/Alert.vue';
import { RefinanceStepMixin } from './../mixins/RefinanceStepMixin';
import { RefinanceFiltersMixin } from './../mixins/RefinanceFiltersMixin';

export default {
    name: 'RefinanceUnavailable',
    mixins: [RefinanceStepMixin, RefinanceFiltersMixin],
    components: {
        PaymentsCompletedProgressBar,
        AppBoxContent,
        FontBold,
        Alert,
    },

    data() {
        return {
        }
    },
}
</script>
